import { ReactElement } from 'react';
import { formatNumber } from '../breakdownFormat';
import FormulaRow from './FormulaRow';

interface Props {
  aeroPower: number;
  riderPower: number;
  kineticPower: number;
  gravitationalPower: number;
  rollingPower: number;
  rollingWork: number;
  duration: number;
  pipelineRollingPower?: number;
}

export default function RollingPowerSection({
  aeroPower,
  riderPower,
  kineticPower,
  gravitationalPower,
  rollingPower,
  rollingWork,
  duration,
  pipelineRollingPower,
}: Props): ReactElement {
  // Pipeline reports Rolling_PG3 as a signed (negative) drag value. Here the client
  // computes it as a positive magnitude. Convert to magnitude for a direct compare.
  const pipelineMag = Number.isFinite(pipelineRollingPower as number)
    ? Math.abs(pipelineRollingPower as number)
    : NaN;
  const delta = Number.isFinite(pipelineMag) ? rollingPower - pipelineMag : NaN;
  const deltaPct = Number.isFinite(delta) && pipelineMag !== 0
    ? (delta / pipelineMag) * 100
    : NaN;

  return (
    <div className="cr-py-2">
      <p className="cr-text-sm cr-text-gray-500 cr-mb-4">
        Energy balance solved for rolling resistance. With Aero predicted from the adjusted CdA,
        rolling power is whatever closes the balance — the magnitude of energy dissipated by
        the tyres and vibration.
      </p>

      <FormulaRow
        className="cr-mb-4"
        inputs={[
          { header: '−Aero', value: `${formatNumber(-aeroPower, 1)} W` },
          { header: '+ Rider', value: `${formatNumber(riderPower, 1)} W` },
          { header: '+ Kinetic', value: `${formatNumber(kineticPower, 1)} W` },
          { header: '+ Gravitational', value: `${formatNumber(gravitationalPower, 1)} W` },
        ]}
        result={{ header: 'Rolling power', value: `${formatNumber(rollingPower, 1)} W` }}
      />
      <FormulaRow
        inputs={[
          { header: 'Rolling power', value: `${formatNumber(rollingPower, 1)} W` },
          { header: '× Time', value: `${formatNumber(duration, 2)} s` },
        ]}
        result={{ header: 'Rolling work', value: `${formatNumber(rollingWork, 0)} J` }}
      />

      {Number.isFinite(pipelineMag) && (
        <div className="cr-mt-3 cr-pt-3 cr-border-t cr-border-gray-200 cr-text-sm">
          <div className="cr-text-gray-700 cr-font-medium cr-mb-1">Pipeline comparison</div>
          <div className="cr-grid cr-grid-cols-3 cr-gap-3 cr-font-mono cr-text-gray-700">
            <div>
              <div className="cr-text-xs cr-text-gray-500">Pipeline (|Rolling_PG3|)</div>
              <div>{formatNumber(pipelineMag, 1)} W</div>
            </div>
            <div>
              <div className="cr-text-xs cr-text-gray-500">Client (energy balance)</div>
              <div>{formatNumber(rollingPower, 1)} W</div>
            </div>
            <div>
              <div className="cr-text-xs cr-text-gray-500">Δ (client − pipeline)</div>
              <div className={Math.abs(deltaPct) > 1 ? 'cr-text-amber-700' : ''}>
                {formatNumber(delta, 1)} W
                {Number.isFinite(deltaPct) && (
                  <span className="cr-ml-2 cr-text-xs">
                    ({deltaPct >= 0 ? '+' : ''}{formatNumber(deltaPct, 2)}%)
                  </span>
                )}
              </div>
            </div>
          </div>
          <p className="cr-text-xs cr-text-gray-500 cr-mt-2 cr-italic">
            The pipeline value uses the per-traverse <em>measured</em> Crr to compute rolling
            resistance directly. The client value above instead solves the energy balance with
            Aero held to the run-level adjusted CdA. The difference quantifies how much aero
            noise enters the per-traverse Crr reading.
          </p>
        </div>
      )}
    </div>
  );
}
